import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { DevModeProvider } from './contexts/DevModeContext';
import { Toaster } from 'sonner';

import Landing from './pages/Landing';
import Auth from './pages/Auth';
import Login from './pages/Login';
import Register from './pages/Register';
import MissionPage from './pages/MissionPage';
import Dashboard from './pages/Dashboard';
import Projects from './pages/Projects';
import Developer from './pages/Developer';
import DeveloperChat from './pages/DeveloperChat';
import DeveloperGithub from './pages/DeveloperGithub';
import PaymentAccounts from './pages/PaymentAccounts';
import Forum from './pages/Forum';
import CompanyProfile from './pages/CompanyProfile';
import AuditLogs from './pages/AuditLogs';
import ProjectData from './pages/ProjectData';
import TechnicalData from './pages/TechnicalData';
import SmartCalc from './pages/SmartCalc';
import PhotovoltaicCalc from './pages/PhotovoltaicCalc';
import Verification from './pages/Verification';
import AIAssistantPage from './pages/AIAssistantPage';
import AuditPage from './pages/AuditPage';
import InternalCertifications from './pages/InternalCertifications';
import EmailComposer from './pages/EmailComposer';
import Templates from './pages/Templates';
import TemplateEditor from './pages/TemplateEditor';
import Stamps from './pages/Stamps';
import Certificates from './pages/Certificates';
import Documents from './pages/Documents';
import Pricing from './pages/Pricing';
import Billing from './pages/Billing';
import Settings from './pages/Settings';
import AuthCallback from './pages/AuthCallback';
import Termeni from './pages/Termeni';
import Confidentialitate from './pages/Confidentialitate';
import Gdpr from './pages/Gdpr';
import IndustriesHub from './pages/IndustriesHub';
import IndustryDetail from './pages/IndustryDetail';
import FeaturesHub from './pages/FeaturesHub';
import FeatureDetail from './pages/FeatureDetail';
import DeveloperProgres from './pages/DeveloperProgres';
import AdminConfig from './pages/AdminConfig';
import AdminEssentials from './pages/AdminEssentials';
import AIAgents from './pages/AIAgents';
import SEAPAlerts from './pages/SEAPAlerts';
import CRMSubscribers from './pages/CRMSubscribers';
import ANAFInvoicing from './pages/ANAFInvoicing';
import PublicStatus from './pages/PublicStatus';
import EnergyAdvisor from './pages/EnergyAdvisor';
import Jobs from './pages/Jobs';
import Contracts from './pages/Contracts';
import Personas from './pages/Personas';
import GasNaturalProject from './pages/GasNaturalProject';
import GasRecipients from './pages/GasRecipients';
import Subscribers from './pages/Subscribers';
import VerifyGasProject from './pages/VerifyGasProject';
import Clients from './pages/Clients';
import Companies from './pages/Companies';
import Inside from './pages/Inside';
import ImplementationQueue from './pages/ImplementationQueue';
import SelfCheck from './pages/SelfCheck';
import ProductSkeleton from './pages/ProductSkeleton';
import GasNaturalProjectV2 from './pages/GasNaturalProjectV2';
import GasNaturalStudio from './pages/GasNaturalStudio';
import VerificatorWorkspace from './pages/VerificatorWorkspace';
import Parteneri from './pages/Parteneri';
import AdminUsers from './pages/AdminUsers';
import VoturiCNP from './pages/VoturiCNP';
import RivieraRomaneasca from './pages/RivieraRomaneasca';
import Transparenta from './pages/Transparenta';
import ComertLogistica from './pages/ComertLogistica';
import FabriciUzine from './pages/FabriciUzine';
import Sponsorizeaza from './pages/Sponsorizeaza';
import Constructii from './pages/Constructii';
import DocumentatieElectronica from './pages/DocumentatieElectronica';
import Contact from './pages/Contact';
import AfaceriB2B from './pages/AfaceriB2B';
import InvestitiiBursa from './pages/InvestitiiBursa';
import EcosystemServicePage from './pages/EcosystemServicePage';
import IndustrySeoPage from './pages/IndustrySeoPage';
// V7.0 — High-end ecosystem pages
import HomePageV7 from './pages/HomePageV7';
import DocumentatieIndustriiPage from './pages/DocumentatieIndustriiPage';
import MarketplacePage from './pages/MarketplacePage';
import PiataProducatori from './pages/PiataProducatori';
import PiataProdus from './pages/PiataProdus';
import PiataVinde from './pages/PiataVinde';
import PiataCont from './pages/PiataCont';
import RealEstatePage from './pages/RealEstatePage';
import ForumPage from './pages/ForumPage';
import ServiciiPage from './pages/ServiciiPage';
import PlanuriDepartamente from './pages/PlanuriDepartamente';
import ComisioaneTarife from './pages/ComisioaneTarife';
import UpgradeGate from './components/UpgradeGate';
// V11.0 — 6 Coming Soon services (curierat, transport, mediu, spitale, caritabile, biserica)
import ComingSoonService from './pages/ComingSoonService';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="h-screen flex items-center justify-center text-sm text-gray-500">Se încarcă…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function HomeRedirect() {
  // V10.3 — Logged-in users can ALSO see Landing (per user request: "click pe logo
  // sa duca la pagina de prezentare"). Post-login redirect is handled separately
  // by Login.jsx → navigate('/dashboard'), not here.
  return <Landing />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<HomeRedirect />} />
      {/* V13.0 — Unified auth page (Login + Register combined per user command CMD-03).
          Old /login and /register still work but redirect into /auth for continuity. */}
      <Route path="/auth" element={<Auth />} />
      <Route path="/login" element={<Navigate to="/auth?mode=signin" replace />} />
      <Route path="/register" element={<Navigate to="/auth?mode=signup" replace />} />
      {/* Kept exports for direct linking / testing safety */}
      <Route path="/legacy-login" element={<Login />} />
      <Route path="/legacy-register" element={<Register />} />
      {/* V1.2 — Mission pages (petitii/jurnalism/renovare-blocuri) */}
      <Route path="/petitii-campus"   element={<MissionPage />} />
      <Route path="/petitii-sociale"  element={<MissionPage />} />
      <Route path="/jurnalism"        element={<MissionPage />} />
      <Route path="/renovare-blocuri" element={<MissionPage />} />
      <Route path="/pricing" element={<Pricing />} />
      {/* V11.2 — Misiuni EPD Next-Gen (presentation sites — public, no auth) */}
      <Route path="/voturi-cnp" element={<VoturiCNP />} />
      <Route path="/riviera-romaneasca" element={<RivieraRomaneasca />} />
      <Route path="/transparenta" element={<Transparenta />} />
      <Route path="/termeni" element={<Termeni />} />
      <Route path="/confidentialitate" element={<Confidentialitate />} />
      <Route path="/gdpr" element={<Gdpr />} />
      <Route path="/pentru/:role" element={<Personas />} />
      {/* V12.1 — Gas Natural Studio is PUBLIC (free viewing & data entry, export gated by plan).
          Per user request: "platforma gaze naturale nu ar trebui sa fie protejata de logare, ci libera pentru testare si vizualizare." */}
      <Route path="/gaze-naturale" element={<GasNaturalStudio />} />
      <Route path="/gaze-naturale/:id" element={<GasNaturalStudio />} />
      <Route path="/gaze-naturale-studio" element={<GasNaturalStudio />} />
      <Route path="/gaze-naturale-studio/:id" element={<GasNaturalStudio />} />
      <Route path="/gaze-naturale-v2" element={<ProtectedRoute><GasNaturalProjectV2 /></ProtectedRoute>} />
      <Route path="/gaze-naturale-v2/:pid" element={<ProtectedRoute><GasNaturalProjectV2 /></ProtectedRoute>} />
      <Route path="/gaze-naturale-v1/:pid" element={<ProtectedRoute><GasNaturalProject /></ProtectedRoute>} />
      <Route path="/gaze-naturale/recipients" element={<ProtectedRoute><GasRecipients /></ProtectedRoute>} />
      <Route path="/subscribers" element={<ProtectedRoute><Subscribers /></ProtectedRoute>} />
      <Route path="/verify/gas-project/:pid" element={<VerifyGasProject />} />

      {/* V12.0 — Workspace Verificator VGD / RTE (plans 1000 EUR/lună) */}
      <Route path="/verificator/inbox" element={<ProtectedRoute><VerificatorWorkspace /></ProtectedRoute>} />
      <Route path="/verificator/ledger" element={<ProtectedRoute><VerificatorWorkspace /></ProtectedRoute>} />

      {/* V12.2 — Parteneri & Colaborări (public listing, create requires login) */}
      <Route path="/parteneri" element={<Parteneri />} />

      {/* V12.6 — Admin: evidență reală conturi (developer only) */}
      <Route path="/admin/conturi" element={<ProtectedRoute><AdminUsers /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute><AdminUsers /></ProtectedRoute>} />

      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
      <Route path="/proiecte" element={<ProtectedRoute><Projects /></ProtectedRoute>} />
      <Route path="/developer" element={<ProtectedRoute><Developer /></ProtectedRoute>} />
      <Route path="/developer/chat" element={<ProtectedRoute><DeveloperChat /></ProtectedRoute>} />
      <Route path="/developer/github" element={<ProtectedRoute><DeveloperGithub /></ProtectedRoute>} />
      <Route path="/admin/payment-accounts" element={<ProtectedRoute><PaymentAccounts /></ProtectedRoute>} />
      <Route path="/forum" element={<ProtectedRoute><Forum /></ProtectedRoute>} />
      <Route path="/forum/:threadId" element={<ProtectedRoute><Forum /></ProtectedRoute>} />
      <Route path="/company" element={<ProtectedRoute><CompanyProfile /></ProtectedRoute>} />
      <Route path="/logs" element={<ProtectedRoute><AuditLogs /></ProtectedRoute>} />
      <Route path="/proiect" element={<ProtectedRoute><ProjectData /></ProtectedRoute>} />
      <Route path="/tehnice" element={<ProtectedRoute><TechnicalData /></ProtectedRoute>} />
      <Route path="/calcul" element={<ProtectedRoute><SmartCalc /></ProtectedRoute>} />
      <Route path="/fotovoltaic" element={<ProtectedRoute><PhotovoltaicCalc /></ProtectedRoute>} />
      <Route path="/verifica" element={<ProtectedRoute><Verification /></ProtectedRoute>} />
      <Route path="/ai" element={<ProtectedRoute><AIAssistantPage /></ProtectedRoute>} />
      <Route path="/audit" element={<ProtectedRoute><AuditPage /></ProtectedRoute>} />
      <Route path="/certificari" element={<ProtectedRoute><InternalCertifications /></ProtectedRoute>} />
      <Route path="/templates/:id" element={<ProtectedRoute><TemplateEditor /></ProtectedRoute>} />
      <Route path="/documents" element={<ProtectedRoute><Documents /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />

      {/* Industries Hub + per-industry skeleton */}
      <Route path="/industrii" element={<ProtectedRoute><IndustriesHub /></ProtectedRoute>} />
      <Route path="/industrii/:industryId" element={<ProtectedRoute><IndustryDetail /></ProtectedRoute>} />

      {/* Features Hub (Feat-uri vision) + skeleton sub-pages */}
      <Route path="/feat-uri" element={<ProtectedRoute><FeaturesHub /></ProtectedRoute>} />
      <Route path="/feat-uri/:featureId" element={<ProtectedRoute><FeatureDetail /></ProtectedRoute>} />

      {/* Progress page (developer-only) */}
      <Route path="/developer/progres" element={<ProtectedRoute><DeveloperProgres /></ProtectedRoute>} />
      <Route path="/admin/config" element={<ProtectedRoute><AdminConfig /></ProtectedRoute>} />
      <Route path="/admin/essentials" element={<ProtectedRoute><AdminEssentials /></ProtectedRoute>} />
      <Route path="/status" element={<PublicStatus />} />
      <Route path="/jobs" element={<Jobs />} />

      {/* EPD Vision — Inside Full + Implementation Queue + Self Check + Product Skeleton */}
      <Route path="/inside" element={<ProtectedRoute><Inside /></ProtectedRoute>} />
      <Route path="/queue" element={<ProtectedRoute><ImplementationQueue /></ProtectedRoute>} />
      <Route path="/self-check" element={<ProtectedRoute><SelfCheck /></ProtectedRoute>} />
      <Route path="/skeleton" element={<ProtectedRoute><ProductSkeleton /></ProtectedRoute>} />

      {/* V7.0 — Ecosystem pages (Home + 5 ecosisteme) */}
      <Route path="/acasa" element={<ProtectedRoute><HomePageV7 /></ProtectedRoute>} />
      <Route path="/documentatie-industrii" element={<ProtectedRoute><DocumentatieIndustriiPage /></ProtectedRoute>} />
      <Route path="/marketplace" element={<ProtectedRoute><MarketplacePage /></ProtectedRoute>} />
      <Route path="/imobiliare" element={<ProtectedRoute><RealEstatePage /></ProtectedRoute>} />
      <Route path="/forum-v7" element={<ProtectedRoute><ForumPage /></ProtectedRoute>} />
      <Route path="/servicii" element={<ProtectedRoute><ServiciiPage /></ProtectedRoute>} />
      <Route path="/smart-pricing" element={<ProtectedRoute><ServiciiPage /></ProtectedRoute>} />
      <Route path="/planuri-departamente" element={<ProtectedRoute><PlanuriDepartamente /></ProtectedRoute>} />
      <Route path="/comisioane-tarife" element={<ProtectedRoute><ComisioaneTarife /></ProtectedRoute>} />

      {/* V9.0 — Pagini ecosistem extins (Comerț + Logistică, Fabrici + Uzine) */}
      <Route path="/comert-logistica" element={<ProtectedRoute><ComertLogistica /></ProtectedRoute>} />
      <Route path="/fabrici-uzine" element={<ProtectedRoute><FabriciUzine /></ProtectedRoute>} />

      {/* V9.5 — Sponsorizează cauza EPD (public, fără autentificare) */}
      <Route path="/sponsorizeaza" element={<Sponsorizeaza />} />

      {/* V10.1 — Pagini SEO public pentru Google indexing (energyprojectdesign.com) */}
      <Route path="/constructii" element={<Constructii />} />
      <Route path="/documentatie-electronica" element={<DocumentatieElectronica />} />
      <Route path="/contact" element={<Contact />} />
      {/* V13.7 — Afaceri B2B (public, cerință explicită user — subpagină în navigația de sus) */}
      <Route path="/afaceri-b2b" element={<AfaceriB2B />} />
      {/* V13.9 — Investiții BVB (director companii energetice românești listate la Bursa de Valori București) */}
      <Route path="/investitii-bursa" element={<InvestitiiBursa />} />

      {/* V13.12 — Piața Micilor Producători (listare gratuită, comision 1%) */}
      <Route path="/piata" element={<PiataProducatori />} />
      <Route path="/piata/produs/:pid" element={<PiataProdus />} />
      <Route path="/piata/vinde" element={<PiataVinde />} />
      <Route path="/piata/cont" element={<ProtectedRoute><PiataCont /></ProtectedRoute>} />
      {/* V13.11 — Pagini dinamice per serviciu ecosistem (40+ SEO landing pages) */}
      <Route path="/e/:serviceId" element={<EcosystemServicePage />} />

      {/* V10.6 — 15+ industry SEO pages (public, multilingual, hreflang) */}
      <Route path="/investitori" element={<IndustrySeoPage />} />
      <Route path="/aviatie" element={<IndustrySeoPage />} />
      <Route path="/airflight" element={<IndustrySeoPage />} />
      <Route path="/spatial" element={<IndustrySeoPage />} />
      <Route path="/spaceflight" element={<IndustrySeoPage />} />
      <Route path="/satelite" element={<IndustrySeoPage />} />
      <Route path="/drone-uav" element={<IndustrySeoPage />} />
      <Route path="/electric" element={<IndustrySeoPage />} />
      <Route path="/fotovoltaice" element={<IndustrySeoPage />} />
      <Route path="/telecom" element={<IndustrySeoPage />} />
      <Route path="/hvac" element={<IndustrySeoPage />} />
      <Route path="/apa-canal" element={<IndustrySeoPage />} />
      <Route path="/feroviar" element={<IndustrySeoPage />} />
      <Route path="/sanatate" element={<IndustrySeoPage />} />
      <Route path="/sport-stadioane" element={<IndustrySeoPage />} />

      {/* Pages with UpgradeGate (modal upgrade când userul nu are plan suficient) */}
      <Route path="/anaf-efactura" element={<ProtectedRoute><UpgradeGate path="/anaf-efactura"><ANAFInvoicing /></UpgradeGate></ProtectedRoute>} />
      <Route path="/seap-alerts" element={<ProtectedRoute><UpgradeGate path="/seap-alerts"><SEAPAlerts /></UpgradeGate></ProtectedRoute>} />
      <Route path="/ai-agents" element={<ProtectedRoute><UpgradeGate path="/ai-agents"><AIAgents /></UpgradeGate></ProtectedRoute>} />
      <Route path="/consultant-ai" element={<ProtectedRoute><UpgradeGate path="/consultant-ai"><EnergyAdvisor /></UpgradeGate></ProtectedRoute>} />
      <Route path="/crm-abonati" element={<ProtectedRoute><UpgradeGate path="/crm-abonati"><CRMSubscribers /></UpgradeGate></ProtectedRoute>} />
      <Route path="/companies" element={<ProtectedRoute><UpgradeGate path="/companies"><Companies /></UpgradeGate></ProtectedRoute>} />
      <Route path="/contracts" element={<ProtectedRoute><UpgradeGate path="/contracts"><Contracts /></UpgradeGate></ProtectedRoute>} />
      <Route path="/clients" element={<ProtectedRoute><UpgradeGate path="/clients"><Clients /></UpgradeGate></ProtectedRoute>} />
      <Route path="/templates" element={<ProtectedRoute><UpgradeGate path="/templates"><Templates /></UpgradeGate></ProtectedRoute>} />
      <Route path="/stamps" element={<ProtectedRoute><UpgradeGate path="/stamps"><Stamps /></UpgradeGate></ProtectedRoute>} />
      <Route path="/certificate" element={<ProtectedRoute><UpgradeGate path="/certificate"><Certificates /></UpgradeGate></ProtectedRoute>} />
      <Route path="/email" element={<ProtectedRoute><UpgradeGate path="/email"><EmailComposer /></UpgradeGate></ProtectedRoute>} />

      {/* V11.0 — 6 Coming Soon services (public, link-uite din Landing) */}
      <Route path="/curierat" element={<ComingSoonService slug="curierat" />} />
      <Route path="/transport-persoane" element={<ComingSoonService slug="transport-persoane" />} />
      <Route path="/mediu" element={<ComingSoonService slug="mediu" />} />
      <Route path="/spitale" element={<ComingSoonService slug="spitale" />} />
      <Route path="/caritabile" element={<ComingSoonService slug="caritabile" />} />
      <Route path="/biserica" element={<ComingSoonService slug="biserica" />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  // V12.4 — Native Google OAuth Client ID (own Google Cloud project, "Energy Project Design" branding).
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH.
  const GOOGLE_CLIENT_ID = '656281504261-32farph07s7if0cgqgec7a0vvnsjrmic.apps.googleusercontent.com';
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <AuthProvider>
        <DevModeProvider>
          <BrowserRouter>
            <AppRouter />
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </DevModeProvider>
      </AuthProvider>
    </GoogleOAuthProvider>
  );
}
